import { z } from 'zod';

const envSchema = z.object({
  VITE_API_BASE_URL: z.string().url(),
  // Central auth API (SignCare.Auth backend) — used for API calls.
  // Dev: http://localhost:5003 | Staging: https://uat-identity.signcare.io | Prod: https://identity.signcare.io
  VITE_AUTH_URL: z.string().url(),
  // Central auth frontend (login/signup UI) — used for browser redirects.
  // Dev: http://localhost:5004 | Staging/Prod: same as VITE_AUTH_URL
  VITE_AUTH_FRONTEND_URL: z.string().url(),
  // Console frontend (SignCare.Console SPA) — used for browser redirects (e.g. the
  // super-admin "Edit in Console" link from API Pricing → /admin/catalog). Optional:
  // when unset, the deep-link button is simply hidden (nothing else depends on it).
  // Dev: http://localhost:5173 | Staging/Prod: set per deploy target.
  VITE_CONSOLE_URL: z.string().url().optional(),
  // Syncfusion licence key for the template designer's Word editor. Optional so the
  // rest of the app boots without it; when absent the editor renders with a trial
  // banner instead of failing. Must match the installed major version (28.x).
  VITE_SYNCFUSION_LICENSE_KEY: z.string().optional(),
  // NOTE: there is deliberately no VITE_DOC_EDITOR_SERVICE_URL. The Word editor's
  // converter endpoint is derived at runtime from VITE_API_BASE_URL + the active
  // tenant (see TemplateDesignerScreen). As a build-time variable it could not carry
  // the tenant-scoped path at all, and it silently diverged between local and
  // deployed environments until it pointed at a host that served nothing.
  VITE_APP_ENV: z.enum(['development', 'staging', 'production']).default('development'),
  VITE_LOG_LEVEL: z.enum(['debug', 'info', 'warn', 'error']).default('info'),
  // Set to "true" to show the TanStack Query DevTools floating button in development.
  // Off by default — each developer opts in via their local .env file.
  VITE_QUERY_DEVTOOLS: z.enum(['true', 'false']).default('false'),
});

const parsed = envSchema.safeParse(import.meta.env);

if (!parsed.success) {
  // biome-ignore lint/suspicious/noConsole: env validation must surface in console at startup
  console.error('Invalid environment variables:', parsed.error.flatten().fieldErrors);
  throw new Error('Invalid environment variables — see console for details.');
}

export const env = parsed.data;
export type Env = typeof env;
